import type { MarketMonitorSourceScope } from './index';
import { validCommentHistoryPlan, type CommentHistoryPlan } from './commentHistory';
import { isDiscoveryDateCapability } from './discoveryDates';
import { nativeHistoryPreviewMatches, nativeHistoryScopesMatch, normalizeNativeDateHistory, validNativeDateHistoryPlan,
    type NativeDateHistoryPlan } from './nativeDateHistory';

export const REDDIT_HISTORY_CAPABILITY = 'reddit.posts.discover_by_keyword';
export const HISTORY_SUPPORT = 'reddit-history/v1';
export const HISTORY_WINDOWS = ['past_hour', 'past_day', 'past_week', 'past_month', 'past_year', 'all_time'] as const;
export type HistoryWindow = typeof HISTORY_WINDOWS[number];
export interface CollectionHistoryPolicy {
    schema_version: typeof HISTORY_SUPPORT;
    mode: 'adaptive_recheck';
    overlap_hours: number;
    max_window: HistoryWindow;
    initial_start_at?: string;
    end_at?: string;
    recovery_horizon_days?: number;
}
export interface MetaAdvertiserHistoryPolicy {
    schema_version: 'meta-advertiser-history/v1';
    mode: 'incremental_recheck';
    overlap_days: 7;
    max_window: 'all';
    initial_start_at: string;
    end_at: string;
}
export interface MetaAdvertiserHistoryPlan {
    contract_version: 'market-meta-advertiser-history-plan/v1';
    evaluated_at: string;
    initial_window: 'all' | 'last24h' | 'last7d' | 'last14d' | 'last30d';
    max_window: 'all';
    gap_codes: string[];
    checkpoint_job_id?: string;
    requested_since: string;
    slice_id: string;
    input: string;
    phase: 'initial_history' | 'incremental_recheck' | 'needs_review';
    action: 'collect' | 'confirm_scope';
    resolved_window?: 'all' | 'last24h' | 'last7d' | 'last14d' | 'last30d';
    reason_code?: typeof HISTORY_REASONS[number];
    dispatch_valid_until?: string;
}
export const HISTORY_REASONS = ['history_time_unverified', 'history_submit_outcome_unresolved',
    'history_gap_needs_review', 'history_window_exceeds_confirmation', 'history_dispatch_window_expired'] as const;
export interface CollectionHistoryPlan {
    contract_version: 'market-collection-history-plan/v1';
    evaluated_at: string;
    initial_window: HistoryWindow;
    max_window: HistoryWindow;
    coverage_semantics: 'observed_sample_only';
    gap_codes: string[];
    checkpoint_job_id?: string;
    requested_since: string | null;
    slice_id: string;
    input: string;
    phase: 'initial_history' | 'incremental_recheck' | 'gap_recheck' | 'needs_review';
    action: 'collect' | 'confirm_scope';
    resolved_window?: HistoryWindow;
    reason_code?: typeof HISTORY_REASONS[number];
    dispatch_valid_until?: string;
    dispatched_at?: string;
}
export type AnyCollectionHistoryPlan = CollectionHistoryPlan | MetaAdvertiserHistoryPlan | NativeDateHistoryPlan | CommentHistoryPlan;
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const window = (value: unknown): value is HistoryWindow => HISTORY_WINDOWS.includes(value as HistoryWindow);
const date = (value: unknown): value is string => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
const metaWindows = ['all', 'last24h', 'last7d', 'last14d', 'last30d'];

export function normalizeMetaAdvertiserHistory(value: unknown): MetaAdvertiserHistoryPolicy | null {
    if (value == null) return null;
    if (!record(value) || Object.keys(value).some(key => !['schema_version', 'mode', 'overlap_days',
        'max_window', 'initial_start_at', 'end_at'].includes(key))
        || value.schema_version !== 'meta-advertiser-history/v1' || value.mode !== 'incremental_recheck'
        || value.overlap_days !== 7 || value.max_window !== 'all' || !date(value.initial_start_at)
        || !date(value.end_at) || Date.parse(value.initial_start_at) >= Date.parse(value.end_at))
        throw new Error('Invalid Meta advertiser history policy');
    return value as unknown as MetaAdvertiserHistoryPolicy;
}

export function normalizeCollectionHistory(value: unknown, initial: unknown = 'past_week'): CollectionHistoryPolicy | null {
    if (value == null) return null;
    const keys = record(value) ? Object.keys(value) : [];
    if (!record(value) || keys.some(key => !['schema_version', 'mode', 'overlap_hours', 'max_window',
        'initial_start_at', 'end_at', 'recovery_horizon_days'].includes(key)) || value.schema_version !== HISTORY_SUPPORT
        || value.mode !== 'adaptive_recheck' || !window(initial) || !window(value.max_window)
        || !Number.isInteger(value.overlap_hours) || Number(value.overlap_hours) < 1 || Number(value.overlap_hours) > 168
        || HISTORY_WINDOWS.indexOf(value.max_window) < HISTORY_WINDOWS.indexOf(initial)
        // Mirrors conservative planning bounds, not Provider calendar guarantees.
        || [2700, 82800, 518400, 2332800, 31449600, Infinity][HISTORY_WINDOWS.indexOf(value.max_window)] <= Number(value.overlap_hours) * 3600 + 300
        || (value.initial_start_at === undefined) !== (value.end_at === undefined)
        || value.initial_start_at !== undefined && (!date(value.initial_start_at) || !date(value.end_at)
            || Date.parse(value.initial_start_at) >= Date.parse(String(value.end_at))
            || !Number.isInteger(value.recovery_horizon_days) || Number(value.recovery_horizon_days) < 1
            || Number(value.recovery_horizon_days) > 30))
        throw new Error('Invalid collection history policy');
    return {
        schema_version: HISTORY_SUPPORT,
        mode: 'adaptive_recheck',
        overlap_hours: Number(value.overlap_hours),
        max_window: value.max_window,
        ...(value.initial_start_at === undefined ? {} : {
            initial_start_at: value.initial_start_at as string,
            end_at: value.end_at as string,
            recovery_horizon_days: Number(value.recovery_horizon_days),
        }),
    };
}

const normalizeHistoryForScope = (scope: MarketMonitorSourceScope) =>
    scope.capability === 'meta.ads.discover_by_advertiser'
        ? normalizeMetaAdvertiserHistory(scope.collection_history)
        : normalizeCollectionHistory(scope.collection_history, scope.post_window ?? 'past_week');

export function validCollectionHistories(scopes: unknown): boolean {
    if (scopes === undefined) return true;
    if (!Array.isArray(scopes)) return false;
    try {
        return scopes.every(scope => record(scope) && (scope.collection_history == null
            || (scope.capability === REDDIT_HISTORY_CAPABILITY && scope.source_system === 'reddit'
                ? !!normalizeCollectionHistory(scope.collection_history, scope.post_window ?? 'past_week')
                : scope.capability === 'meta.ads.discover_by_advertiser' && scope.source_system === 'meta_ad_library'
                    ? !!normalizeMetaAdvertiserHistory(scope.collection_history)
                : typeof scope.capability === 'string' && isDiscoveryDateCapability(scope.capability)
                    && scope.source_system === scope.capability.split('.')[0] && !!normalizeNativeDateHistory(scope.collection_history, scope))));
    } catch { return false; }
}

export const requestsCollectionHistory = (scopes: MarketMonitorSourceScope[]) => scopes.some(scope => scope.collection_history !== undefined);

/** Explicit null is a deliberate clear and must also be echoed, never treated as omission. */
export function collectionHistoryScopesMatch(requested: MarketMonitorSourceScope[], echoed: unknown, previous: MarketMonitorSourceScope[] = []): boolean {
    if (!nativeHistoryScopesMatch(requested, echoed, previous)) return false;
    requested = requested.filter(scope => !isDiscoveryDateCapability(scope.capability));
    if (!requestsCollectionHistory(requested)) return true;
    if (!Array.isArray(echoed) || !validCollectionHistories(echoed)) return false;
    try {
        return requested.filter(scope => scope.collection_history !== undefined).every(scope => {
            const matches = echoed.filter(other => record(other) && other.capability === scope.capability
                && other.source_system === scope.source_system && record(other.scope)
                && other.scope.kind === scope.scope.kind && other.scope.value === scope.scope.value);
            const echoedScope = matches[0] as unknown as MarketMonitorSourceScope;
            return matches.length === 1
                && (scope.capability === 'meta.ads.discover_by_advertiser'
                    || (matches[0].post_window ?? 'past_week') === (scope.post_window ?? 'past_week'))
                && JSON.stringify(normalizeHistoryForScope(scope))
                === JSON.stringify(normalizeHistoryForScope(echoedScope));
        });
    } catch { return false; }
}

export function validCollectionHistoryPlans(value: unknown): value is AnyCollectionHistoryPlan[] | undefined {
    if (value === undefined) return true;
    if (!Array.isArray(value)) return false;
    return value.every(plan => validCommentHistoryPlan(plan) || validNativeDateHistoryPlan(plan)
        || validMetaAdvertiserHistoryPlan(plan) || record(plan) && plan.contract_version === 'market-collection-history-plan/v1'
        && date(plan.evaluated_at) && window(plan.initial_window) && window(plan.max_window)
        && plan.coverage_semantics === 'observed_sample_only' && typeof plan.slice_id === 'string' && !!plan.slice_id
        && typeof plan.input === 'string' && !!plan.input.trim()
        && Array.isArray(plan.gap_codes) && plan.gap_codes.every(code => typeof code === 'string')
        && (plan.requested_since === null || date(plan.requested_since))
        && (plan.dispatched_at === undefined || date(plan.dispatched_at))
        && (plan.checkpoint_job_id === undefined || typeof plan.checkpoint_job_id === 'string')
        && (plan.action === 'collect'
            ? ['initial_history', 'incremental_recheck', 'gap_recheck'].includes(String(plan.phase))
                && window(plan.resolved_window) && HISTORY_WINDOWS.indexOf(plan.resolved_window) <= HISTORY_WINDOWS.indexOf(plan.max_window)
                && date(plan.dispatch_valid_until)
                && (plan.phase !== 'initial_history' || plan.resolved_window === plan.initial_window)
            : plan.action === 'confirm_scope' && plan.phase === 'needs_review' && HISTORY_REASONS.includes(plan.reason_code as typeof HISTORY_REASONS[number])));
}

function validMetaAdvertiserHistoryPlan(value: unknown): value is MetaAdvertiserHistoryPlan {
    if (!record(value) || value.contract_version !== 'market-meta-advertiser-history-plan/v1'
        || !date(value.evaluated_at) || !date(value.requested_since) || typeof value.slice_id !== 'string'
        || !value.slice_id || typeof value.input !== 'string' || !value.input.trim()
        || !metaWindows.includes(String(value.initial_window)) || value.max_window !== 'all'
        || !Array.isArray(value.gap_codes) || !value.gap_codes.every(code => typeof code === 'string')) return false;
    return value.action === 'collect'
        ? ['initial_history', 'incremental_recheck'].includes(String(value.phase))
            && metaWindows.includes(String(value.resolved_window)) && date(value.dispatch_valid_until)
        : value.action === 'confirm_scope' && value.phase === 'needs_review'
            && HISTORY_REASONS.includes(value.reason_code as typeof HISTORY_REASONS[number]);
}

/** Requires an execution-support acknowledgement even when clearing the last policy. */
export function collectionHistoryPreviewMatches(requested: MarketMonitorSourceScope[], preview: {
    normalized_monitor: Record<string, unknown>;
    collection_history_support?: unknown;
    native_date_history_support?: unknown;
    collection_history_preview?: unknown;
}, previous: MarketMonitorSourceScope[] = []): boolean {
    if (!nativeHistoryPreviewMatches(requested, preview, previous)) return false;
    requested = requested.filter(scope => !isDiscoveryDateCapability(scope.capability));
    if (!requestsCollectionHistory(requested)) return true;
    if (preview.collection_history_support !== HISTORY_SUPPORT || !collectionHistoryScopesMatch(requested, preview.normalized_monitor.source_scopes)
        || !validCollectionHistoryPlans(preview.collection_history_preview)) return false;
    const enabled = requested.filter(scope => scope.collection_history != null);
    const plans = (preview.collection_history_preview ?? []).filter((plan): plan is CollectionHistoryPlan | MetaAdvertiserHistoryPlan =>
        plan.contract_version === 'market-collection-history-plan/v1'
        || plan.contract_version === 'market-meta-advertiser-history-plan/v1');
    if (!enabled.length) return plans.length === 0;
    const terms = preview.normalized_monitor.terms;
    if (!Array.isArray(terms) || !terms.every(term => record(term) && typeof term.value === 'string')) return false;
    const keywords = terms.filter(term => term.role !== 'exclude').map(term => term.value.trim().toLowerCase());
    const identity = (contract: string, input: string | undefined, initial: string, maximum: string) =>
        JSON.stringify([contract, input, initial, maximum]);
    const expected: string[] = [];
    for (const scope of enabled) {
        const policy = scope.collection_history;
        if (scope.capability === 'meta.ads.discover_by_advertiser') {
            if (policy?.schema_version !== 'meta-advertiser-history/v1') return false;
            expected.push(identity('market-meta-advertiser-history-plan/v1', scope.scope.value?.trim().toLowerCase(),
                scope.advertiser_period ?? 'last7d', policy.max_window));
        } else {
            if (policy?.schema_version !== HISTORY_SUPPORT) return false;
            expected.push(...keywords.map(input => identity('market-collection-history-plan/v1', input,
                scope.post_window ?? 'past_week', policy.max_window)));
        }
    }
    const remaining = [...expected];
    const matches = plans.every(plan => {
        const index = remaining.indexOf(identity(plan.contract_version, plan.input.trim().toLowerCase(),
            plan.initial_window, plan.max_window));
        if (index < 0) return false;
        remaining.splice(index, 1);
        return true;
    });
    return matches && remaining.length === 0 && new Set(plans.map(plan => plan.slice_id)).size === plans.length;
}
